import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useApp } from "../../contexts/AppContext";
import {
  fetchTemplates,
  setCurrentTemplatePage,
  deleteTemplate,
  clearDeleteStatus,
  setTemplateAsDefault,
  clearSetDefaultStatus,
  clearRefetchFlag,
  duplicateTemplate,
  clearDuplicateStatus,
} from "./slice";
import {
  Table,
  Button,
  Tabs,
  Tab,
  Dropdown,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import PageLayout from "../../components/common/PageLayout";
import { ThreeDots } from "react-bootstrap-icons";
import HowItWorksButton from "../../components/common/HowItWorksButton";
import Icon from "@mdi/react";
import { mdiPlus, mdiContentCopy } from "@mdi/js";
import CustomToast from "../../components/common/CustomToast";
import CustomPagination from "../../components/common/Pagination";
import DeleteTemplateConfirmationModal from "./DeleteTemplateConfirmationModal";
import DuplicateTemplateModal from "./DuplicateTemplateModal";
import HowItWorksModal from "./HowItWorksModal";
import NarrativeLibrary from "./NarrativeLibrary";
import BadgeWithTooltip from "./BadgeWithTooltip";
import { getBannerImage } from "./bannerHelpers";
import UpgradePlanModal from "../../components/WebApp/GlobalNavBar/UpgradePlanModal";
import formatDate from "./utils/formatDate";
import "./templates.css";
import { useInitialFetch } from "../../hooks/useInitialFetch";
import { usePartialLoading } from "../../hooks/usePartialLoading";

const Templates = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    device: { isMobile, isTablet },
    user: { profile },
  } = useApp();

  // Redux state
  const {
    templates,
    templatesCount,
    getTemplatesStatus,
    currentTemplatePage,
    deleteTemplateStatus,
    deleteTemplateError,
    setDefaultStatus,
    setDefaultError,
    shouldRefetchTemplates,
    duplicateTemplateStatus,
    duplicateTemplateError,
    duplicatedTemplateId,
  } = useSelector((state) => state.templates);

  const headerRef = useRef(null);
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);

  // Local state management
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [showHowItWorksModal, setShowHowItWorksModal] = useState(false);
  const [
    showDeleteTemplateConfirmationModal,
    setShowDeleteTemplateConfirmationModal,
  ] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [key, setKey] = useState("section-templates");
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicatingTemplateId, setDuplicatingTemplateId] = useState(null);
  const templatesPerPage = 10;

  const templatesLoading = usePartialLoading("templates-content");
  const { startLoading, stopLoading } = templatesLoading;

  // Clean up duplicate state on unmount
  useEffect(() => {
    return () => {
      dispatch(clearDuplicateStatus());
    };
  }, [dispatch]);

  // Fetch templates on component mount
  useInitialFetch(
    () => {
      templatesLoading.startLoading();
      dispatch(fetchTemplates(currentTemplatePage));
    },
    { skip: templates.length > 0 }
  );

  useEffect(() => {
    if (getTemplatesStatus === "success" || getTemplatesStatus === "failed") {
      stopLoading();
    }
  }, [getTemplatesStatus, stopLoading]);

  // Refetch templates when a new template is created
  useEffect(() => {
    if (shouldRefetchTemplates) {
      startLoading();
      dispatch(fetchTemplates(currentTemplatePage));
      dispatch(clearRefetchFlag());
    }
  }, [shouldRefetchTemplates, dispatch, currentTemplatePage, startLoading]);

  // Handle sticky header
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop || 0;
      const shouldBeSticky = scrollTop > 100;
      setIsHeaderSticky(shouldBeSticky);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Initial check

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Check for success state or refetch flag from navigation
  useEffect(() => {
    if (location.state?.showSuccessMessage || location.state?.shouldRefetch) {
      if (location.state?.showSuccessMessage) {
        setShowSuccessToast(true);
      }
      startLoading();
      dispatch(fetchTemplates(currentTemplatePage));
      window.history.replaceState({}, document.title);
    }
  }, [location.state, dispatch, currentTemplatePage, startLoading]);

  // Handle delete template status changes
  useEffect(() => {
    if (deleteTemplateStatus === "success") {
      setSuccessMessage("Section Template deleted successfully.");
      setShowSuccessToast(true);
      setShowDeleteTemplateConfirmationModal(false);
      setTemplateToDelete(null);
      dispatch(clearDeleteStatus());

      // Refetch the current page - Redux will handle page validation automatically
      startLoading();
      dispatch(fetchTemplates(currentTemplatePage));
    } else if (deleteTemplateStatus === "failed") {
      setErrorMessage(
        deleteTemplateError || "Failed to delete template. Please try again."
      );
      setShowErrorToast(true);
      setShowDeleteTemplateConfirmationModal(false);
      setTemplateToDelete(null);
      dispatch(clearDeleteStatus());
    }
  }, [
    deleteTemplateStatus,
    deleteTemplateError,
    dispatch,
    currentTemplatePage,
    startLoading,
  ]);

  // Handle set default template status changes
  useEffect(() => {
    if (setDefaultStatus === "success") {
      // Find the template name for the success message
      const template = templates.find((t) => t.is_user_default);
      const templateName = template ? template.name : "Template";
      setSuccessMessage(
        `${templateName} is now your default section template.`
      );
      setShowSuccessToast(true);
      dispatch(clearSetDefaultStatus());
    } else if (setDefaultStatus === "failed") {
      setErrorMessage(
        setDefaultError ||
          "Failed to set template as default. Please try again."
      );
      setShowErrorToast(true);
      dispatch(clearSetDefaultStatus());
    }
  }, [setDefaultStatus, setDefaultError, templates, dispatch]);

  // Handle duplicate template status changes
  useEffect(() => {
    if (duplicateTemplateStatus === "success") {
      setDuplicatingTemplateId(null);
      setShowDuplicateModal(false);
      dispatch(clearDuplicateStatus());
      navigate(`/templates/${duplicatedTemplateId}`, {
        state: {
          showSuccessMessage: true,
          message: "Template duplicated successfully.",
        },
      });
    } else if (duplicateTemplateStatus === "failed") {
      setDuplicatingTemplateId(null);
      setErrorMessage(
        duplicateTemplateError ||
          "Failed to duplicate template. Please try again."
      );
      setShowErrorToast(true);
      setShowDuplicateModal(false);
      dispatch(clearDuplicateStatus());
    }
  }, [
    duplicateTemplateStatus,
    duplicateTemplateError,
    duplicatedTemplateId,
    dispatch,
    navigate,
  ]);

  // Handlers
  const handleView = (templateId) => {
    navigate(`/templates/${templateId}`);
  };

  const handleSetAsDefault = (templateId) => {
    // Check if user is on free tier
    if (profile?.subscription?.tier === "Free") {
      setShowUpgradeModal(true);
      return;
    }

    dispatch(setTemplateAsDefault(templateId));
  };

  const handleDelete = (templateId) => {
    setTemplateToDelete(templateId);
    setShowDeleteTemplateConfirmationModal(true);
  };

  const handleConfirmDelete = () => {
    if (templateToDelete) {
      dispatch(deleteTemplate(templateToDelete));
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteTemplateConfirmationModal(false);
    setTemplateToDelete(null);
  };

  const handlePageChange = (pageNumber) => {
    dispatch(setCurrentTemplatePage(pageNumber));
    templatesLoading.startLoading();
    dispatch(fetchTemplates(pageNumber));
  };

  const handleShowHowItWorks = () => {
    setShowHowItWorksModal(true);
  };

  const handleNewTemplate = () => {
    navigate("/templates/new");
  };

  const handleShowDuplicateModal = () => {
    if (profile?.subscription?.tier === "Free") {
      setShowUpgradeModal(true);
      return;
    }
    setShowDuplicateModal(true);
  };

  const handleDuplicateFromModal = (templateId) => {
    dispatch(duplicateTemplate(templateId));
  };

  const handleDirectDuplicate = (templateId) => {
    if (profile?.subscription?.tier === "Free") {
      setShowUpgradeModal(true);
      return;
    }
    setDuplicatingTemplateId(templateId);
    dispatch(duplicateTemplate(templateId));
  };

  // Extract templates data - templates is already an array
  // Sort templates to place system built-in templates at the top
  const displayTemplates = [...(templates || [])].sort((a, b) => {
    // System templates first
    if (a.is_system_default && !b.is_system_default) return -1;
    if (!a.is_system_default && b.is_system_default) return 1;
    // For templates of the same type, maintain original order
    return 0;
  });
  const totalTemplates = templatesCount || 0;

  // Check if we should show the "Add Template" button
  const shouldShowAddTemplateButton =
    displayTemplates.length === 1 &&
    displayTemplates[0]?.is_system_default === true;

  const freeTier = profile?.subscription?.tier === "Free";

  return (
    <PageLayout
      customHeader={
        <div
          ref={headerRef}
          className={`templates-header ${isHeaderSticky ? "sticky" : ""}`}
        >
          <div className="header-title">
            <h2>Smart Templates</h2>
          </div>
          <div className="header-actions">
            <HowItWorksButton onClick={handleShowHowItWorks} />
            <Dropdown className="new-template-dropdown">
              <Dropdown.Toggle variant="primary">
                <Icon path={mdiPlus} size={0.8} className="me-1" />
                New Template
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={handleNewTemplate}>
                  Upload File
                </Dropdown.Item>
                <Dropdown.Item onClick={handleShowDuplicateModal}>
                  Duplicate Existing
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      }
      className="templates-container"
    >
      {/* Free User Upgrade Banner */}
      {freeTier && (
        <button
          className="free-user-banner text-center border-0 bg-transparent w-100 box-shadow-none"
          onClick={() => setShowUpgradeModal(true)}
        >
          <img
            src={getBannerImage(isMobile, isTablet)}
            alt="Free User Upgrade Banner"
            className="img-fluid"
            style={{ maxWidth: "100%", height: "auto" }}
          />
        </button>
      )}

      {/* Tabs */}
      <Tabs id="template-tabs" activeKey={key} onSelect={(k) => setKey(k)}>
        <Tab eventKey="section-templates" title="Section Templates">
          <div
            id="templates-content"
            className="templates-table-container"
            style={{ position: "relative", minHeight: "300px" }}
          >
            {displayTemplates.length === 0 &&
            !templatesLoading.isLoading &&
            getTemplatesStatus !== "loading" &&
            getTemplatesStatus === "success" ? (
              <div className="empty-state">
                <p className="mb-3">
                  Upload a template to match your workflow.
                </p>
                <Dropdown className="new-template-dropdown">
                  <Dropdown.Toggle variant="primary">
                    <Icon path={mdiPlus} size={0.8} className="me-1" />
                    New Template
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item onClick={handleNewTemplate}>
                      Upload File
                    </Dropdown.Item>
                    <Dropdown.Item onClick={handleShowDuplicateModal}>
                      Duplicate Existing
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            ) : (
              displayTemplates.length > 0 && (
                <>
                  {/* Desktop Table View */}
                  <Table className="templates-table d-none d-md-table">
                    <thead>
                      <tr>
                        <th>Template Name</th>
                        <th>Last Modified</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayTemplates.map((template) => (
                        <tr key={template.id}>
                          <td className="template-name">
                            <div>{template.name}</div>
                            <div className="template-badges">
                              {template.is_user_default && (
                                <BadgeWithTooltip
                                  badgeType="default"
                                  badgeProps={{ bg: "primary" }}
                                >
                                  Default
                                </BadgeWithTooltip>
                              )}
                              {template.is_system_default && (
                                <BadgeWithTooltip
                                  badgeType="systemBuiltIn"
                                  badgeProps={{ bg: "secondary" }}
                                >
                                  System Built-in
                                </BadgeWithTooltip>
                              )}
                            </div>
                          </td>
                          <td className="template-date">
                            {template.is_system_default
                              ? "-"
                              : formatDate(template.updated_at)}
                          </td>
                          <td>
                            <div className="action-buttons">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleView(template.id)}
                                className="view-btn"
                              >
                                View
                              </Button>
                              <OverlayTrigger
                                placement="bottom"
                                overlay={
                                  <Tooltip
                                    id={`tooltip-dup-desktop-${template.id}`}
                                  >
                                    Duplicate
                                  </Tooltip>
                                }
                              >
                                <Button
                                  variant="link"
                                  className="copy-btn"
                                  onClick={() =>
                                    handleDirectDuplicate(template.id)
                                  }
                                  disabled={
                                    duplicatingTemplateId === template.id
                                  }
                                  aria-label="Duplicate template"
                                >
                                  <Icon path={mdiContentCopy} size={0.7} />
                                </Button>
                              </OverlayTrigger>
                              <Dropdown>
                                <Dropdown.Toggle
                                  variant="link"
                                  id={`dropdown-desktop-${template.id}`}
                                  className="three-dots-btn"
                                >
                                  <ThreeDots
                                    size={16}
                                    className="text-primary"
                                  />
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                  <Dropdown.Item
                                    onClick={() =>
                                      handleSetAsDefault(template.id)
                                    }
                                    disabled={
                                      setDefaultStatus === "loading" ||
                                      (template.is_system_default &&
                                        template.is_user_default)
                                    }
                                  >
                                    {setDefaultStatus === "loading"
                                      ? "Setting..."
                                      : "Set as Default"}
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    onClick={() => handleDelete(template.id)}
                                    className="text-danger"
                                    disabled={template.is_system_default}
                                  >
                                    Delete
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>

                  {/* Mobile Card View */}
                  <div className="templates-mobile-cards d-md-none">
                    {displayTemplates.map((template) => (
                      <div key={template.id} className="template-card">
                        <div className="template-card-content">
                          <div className="template-card-header">
                            <div className="template-card-name">
                              <div className="template-name-text">
                                {template.name}
                              </div>
                              <div className="template-badges">
                                {template.is_user_default && (
                                  <BadgeWithTooltip
                                    badgeType="default"
                                    badgeProps={{ bg: "primary" }}
                                  >
                                    Default
                                  </BadgeWithTooltip>
                                )}
                                {template.is_system_default && (
                                  <BadgeWithTooltip
                                    badgeType="systemBuiltIn"
                                    badgeProps={{ bg: "secondary" }}
                                  >
                                    System Built-in
                                  </BadgeWithTooltip>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="template-card-footer">
                            <div className="template-card-date">
                              {template.is_system_default
                                ? "-"
                                : formatDate(template.updated_at)}
                            </div>
                            <div className="template-card-actions">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleView(template.id)}
                                className="view-btn"
                              >
                                View
                              </Button>
                              <OverlayTrigger
                                placement="bottom"
                                overlay={
                                  <Tooltip
                                    id={`tooltip-dup-mobile-${template.id}`}
                                  >
                                    Duplicate
                                  </Tooltip>
                                }
                              >
                                <Button
                                  variant="link"
                                  className="copy-btn"
                                  onClick={() =>
                                    handleDirectDuplicate(template.id)
                                  }
                                  disabled={
                                    duplicatingTemplateId === template.id
                                  }
                                  aria-label="Duplicate template"
                                >
                                  <Icon path={mdiContentCopy} size={0.7} />
                                </Button>
                              </OverlayTrigger>
                              <Dropdown>
                                <Dropdown.Toggle
                                  variant="link"
                                  id={`dropdown-mobile-${template.id}`}
                                  className="three-dots-btn"
                                >
                                  <ThreeDots
                                    size={16}
                                    className="text-primary"
                                  />
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                  <Dropdown.Item
                                    onClick={() =>
                                      handleSetAsDefault(template.id)
                                    }
                                    disabled={setDefaultStatus === "loading"}
                                  >
                                    {setDefaultStatus === "loading"
                                      ? "Setting..."
                                      : "Set as Default"}
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    onClick={() => handleDelete(template.id)}
                                    className="text-danger"
                                    disabled={template.is_system_default}
                                  >
                                    Delete
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )
            )}

            {/* Add Template button when only system template exists */}
            {shouldShowAddTemplateButton && (
              <div className="text-center pt-0 empty-state">
                <p className="mb-3">
                  Build your first template to enable real-time report
                  generation.
                </p>
                <Dropdown className="new-template-dropdown">
                  <Dropdown.Toggle variant="primary">
                    <Icon path={mdiPlus} size={0.8} className="me-1" />
                    New Template
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item onClick={handleNewTemplate}>
                      Upload File
                    </Dropdown.Item>
                    <Dropdown.Item onClick={handleShowDuplicateModal}>
                      Duplicate Existing
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            )}

            <div className="pagination-container">
              <CustomPagination
                totalItems={totalTemplates}
                itemsPerPage={templatesPerPage}
                currentPage={currentTemplatePage}
                onPageChange={handlePageChange}
              />
            </div>
          </div>
        </Tab>
        <Tab eventKey="narrative-library" title="Narrative Library">
          <NarrativeLibrary />
        </Tab>
      </Tabs>

      {/* Success Toast */}
      <CustomToast
        show={showSuccessToast}
        onClose={() => setShowSuccessToast(false)}
        message={
          location.state?.message ||
          successMessage ||
          "Template operation completed successfully."
        }
        variant="success"
        position="bottom-end"
        isMobile={isMobile}
        autohide={true}
      />

      {/* Error Toast */}
      <CustomToast
        show={showErrorToast}
        onClose={() => setShowErrorToast(false)}
        message={errorMessage}
        variant="error"
        position="bottom-end"
        isMobile={isMobile}
        autohide={true}
      />

      {/* How It Works Modal */}
      <HowItWorksModal
        show={showHowItWorksModal}
        onHide={() => setShowHowItWorksModal(false)}
      />

      <DeleteTemplateConfirmationModal
        show={showDeleteTemplateConfirmationModal}
        onHide={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        isLoading={deleteTemplateStatus === "loading"}
      />

      <DuplicateTemplateModal
        show={showDuplicateModal}
        onHide={() => setShowDuplicateModal(false)}
        onDuplicate={handleDuplicateFromModal}
        isLoading={duplicateTemplateStatus === "loading"}
      />

      <UpgradePlanModal
        open={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
      />
    </PageLayout>
  );
};

export default Templates;
